import React, { useState, useEffect } from 'react';
import './CurrencyInputField.css';

interface CurrencyInputFieldProps {
  value?: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  precision?: number; // Number of decimal places
}

export const CurrencyInputField: React.FC<CurrencyInputFieldProps> = ({
  value = 0,
  onChange,
  min = 0.01,
  max = 9999999.99,
  placeholder = '0.00',
  disabled = false,
  className = '',
  precision = 2
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Format number to currency display format
  const formatCurrency = (num: number): string => {
    if (num === 0) return '';
    
    // Round to specified precision
    const rounded = Math.round(num * Math.pow(10, precision)) / Math.pow(10, precision);
    
    // Add commas for thousands separators
    return rounded.toLocaleString('en-US', {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision
    });
  };

  // Parse display value back to number
  const parseCurrency = (str: string): number => {
    // Remove commas and parse
    const cleaned = str.replace(/,/g, '');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Calculate cursor position after formatting
  const calculateCursorPosition = (oldValue: string, newValue: string, oldCursorPos: number): number => {
    // Count commas before cursor in old value
    const commasBeforeCursor = (oldValue.substring(0, oldCursorPos).match(/,/g) || []).length;
    // Count commas before cursor in new value
    const newCommasBeforeCursor = (newValue.substring(0, oldCursorPos + (newValue.length - oldValue.length)).match(/,/g) || []).length;
    
    // Adjust cursor position based on comma differences
    return oldCursorPos + (newCommasBeforeCursor - commasBeforeCursor);
  };

  // Initialize display value only when not focused
  useEffect(() => {
    if (!isFocused) {
      if (value > 0) {
        setDisplayValue(formatCurrency(value));
      } else {
        setDisplayValue('');
      }
    }
  }, [value, precision, isFocused]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const oldValue = displayValue;
    const oldCursorPos = e.target.selectionStart || 0;
    let input = e.target.value;
    
    // Remove any non-numeric characters except decimal point
    input = input.replace(/[^\d.]/g, '');
    
    // Ensure only one decimal point
    const parts = input.split('.');
    if (parts.length > 2) {
      input = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Limit decimal places
    if (parts.length === 2 && parts[1].length > precision) {
      input = parts[0] + '.' + parts[1].substring(0, precision);
    }
    
    // Add commas to the integer part while typing
    if (parts.length > 0) {
      const integerPart = parts[0];
      const decimalPart = parts.length > 1 ? '.' + parts[1] : '';
      const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      input = formattedInteger + decimalPart;
    }
    
    setDisplayValue(input);
    
    // Don't call onChange during typing - only on blur
    // This prevents the parent from updating and causing cursor jumps

    // Restore cursor position after state update
    setTimeout(() => {
      if (inputRef.current) {
        const newCursorPos = calculateCursorPosition(oldValue, input, oldCursorPos);
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  // Handle blur - format the display value
  const handleBlur = () => {
    setIsFocused(false);
    const numericValue = parseCurrency(displayValue);
    
    if (numericValue >= min && numericValue <= max) {
      // Format with proper decimal places
      const formattedValue = numericValue.toFixed(precision);
      const finalValue = parseFloat(formattedValue);
      setDisplayValue(formatCurrency(finalValue));
      onChange(finalValue);
    } else if (numericValue < min) {
      setDisplayValue(formatCurrency(min));
      onChange(min);
    } else if (numericValue > max) {
      setDisplayValue(formatCurrency(max));
      onChange(max);
    } else {
      // Handle empty or invalid input
      setDisplayValue('');
      onChange(0);
    }
  };

  // Handle focus
  const handleFocus = () => {
    setIsFocused(true);
    // Show raw number without commas when focused, but keep decimal places
    if (value > 0) {
      setDisplayValue(value.toFixed(precision));
    } else {
      setDisplayValue('');
    }
  };

  // Handle key press for validation
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const char = e.key;
    
    // Allow: backspace, delete, tab, escape, enter
    if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter'].includes(char)) {
      return;
    }
    
    // Allow: numbers
    if (char >= '0' && char <= '9') {
      return;
    }
    
    // Allow: decimal point (only one)
    if (char === '.' && !displayValue.includes('.')) {
      return;
    }
    
    // Prevent all other characters
    e.preventDefault();
  };

  return (
    <div className={`currency-input-field ${className}`}>
      <div className="currency-input-container">
        <span className="currency-symbol">$</span>
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          disabled={disabled}
          className="currency-input"
        />
      </div>

    </div>
  );
};
